import { NewsletterSignup } from "@/components/newsletter/NewsletterSignup";
import { forwardRef } from "react";
import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { Instagram, Youtube, Twitter, Mail, ExternalLink, Heart } from "lucide-react";
import logoShoppingDoBem from "@/assets/logo-shopping-do-bem.webp";
import logoPromovendoOBem from "@/assets/logo-promovendo-o-bem.webp";
import seloMarcaDoBemAsset from "@/assets/selo-marca-do-bem-patrono.png.asset.json";
const seloMarcaDoBemFooter = seloMarcaDoBemAsset.url;
import seloSsl from "@/assets/Selos_SSL.png.asset.json";
import seloGoogle from "@/assets/Selos_Google.png.asset.json";
import { Button } from "@/components/ui/button";
import { resetConsent } from "@/lib/consent";
import { lazyToast } from "@/lib/lazyToast";

const TikTokIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = forwardRef<HTMLElement>((_, ref) => {
  const brandName = "Shopping do Bem";
  const { t } = useTranslation("public");
  return (
    <footer ref={ref} className="bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 text-foreground border-t">
      <div className="container py-12 md:py-16">
        <NewsletterSignup variant="footer" sourceType="footer" className="mb-10" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img
                src={logoShoppingDoBem}
                alt={brandName}
                width={250}
                height={96}
                loading="lazy"
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Links */}
          <div>
            <h2 className="font-display font-semibold mb-4">{t("footer.sections.forYou")}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/como-funciona" className="hover:text-primary transition-colors">
                  {t("footer.links.howItWorks")}
                </Link>
              </li>
              <li>
                <Link to="/marcas" className="hover:text-primary transition-colors">
                  {t("footer.links.partnerStores")}
                </Link>
              </li>
              <li>
                <Link to="/cupons-de-desconto" className="hover:text-primary transition-colors">
                  Cupons de desconto
                </Link>
              </li>
              <li>
                <Link to="/ofertas" className="hover:text-primary transition-colors">
                  Ofertas
                </Link>
              </li>
              <li>
                <Link to="/causas" className="hover:text-primary transition-colors">
                  {t("footer.links.causes")}
                </Link>
              </li>
              <li>
                <Link to="/cadastro" className="hover:text-primary transition-colors">
                  {t("footer.links.register")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display font-semibold mb-4">{t("footer.sections.institutional")}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/sobre-nos" className="hover:text-primary transition-colors">
                  {t("footer.links.about")}
                </Link>
              </li>
              <li>
                <Link to="/como-geramos" className="hover:text-primary transition-colors">
                  {t("footer.links.howWeGenerate")}
                </Link>
              </li>
              <li>
                <Link to="/seja-parceiro" className="hover:text-primary transition-colors">
                  {t("footer.links.partner")}
                </Link>
              </li>
              <li>
                <Link to="/privacidade" className="hover:text-primary transition-colors">
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link to="/termos" className="hover:text-primary transition-colors">
                  {t("footer.links.terms")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => resetConsent()}
                  className="hover:text-primary transition-colors text-left"
                >
                  {t("footer.links.cookies")}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display font-semibold mb-4">{t("footer.sections.contact")}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/contato" className="hover:text-primary transition-colors flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {t("footer.links.contact")}
                </Link>
              </li>
            </ul>
            <div className="flex gap-3 mt-4">
              <a
                href="https://www.instagram.com/shoppingdobem.br/"
                aria-label="Shopping do Bem no Instagram"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@ShoppingdobemBR"
                aria-label="Shopping do Bem no YouTube"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/Shoppingdobem"
                aria-label="Shopping do Bem no X (Twitter)"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@shoppingdobem"
                aria-label="Shopping do Bem no TikTok"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <TikTokIcon />
              </a>
            </div>

            <div className="mt-4 hidden items-center gap-3 md:flex" aria-label="Selos de confiança">
              <button
                type="button"
                aria-label="Conheça o programa Marca do Bem"
                onClick={() => lazyToast.info("O programa Marca do Bem chega em breve.")}
                className="inline-flex h-[100px] w-[100px] shrink-0 items-center justify-center rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <img src={seloMarcaDoBemFooter} alt="Marca do Bem — Selo Patrono Oficial" width={600} height={600} loading="lazy" className="max-h-[100px] max-w-[100px] object-contain" />
              </button>
              <div className="flex flex-col items-start gap-1.5">
                <img src={seloSsl.url} alt="Selo SSL certificado: site 100% seguro" width={150} height={80} loading="lazy" className="h-auto max-h-11 w-auto max-w-24 object-contain" />
                <a
                  href="https://transparencyreport.google.com/safe-browsing/search?url=https:%2F%2Fshoppingdobem.com.br%2F&hl=pt_BR"
                  aria-label="Consultar Shopping do Bem no Google Safe Browsing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <img src={seloGoogle.url} alt="Selo Google Safe Browsing" width={150} height={80} loading="lazy" className="h-auto max-h-11 w-auto max-w-24 object-contain" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:hidden" aria-label="Selos de confiança">
          <button
            type="button"
            aria-label="Conheça o programa Marca do Bem"
            onClick={() => lazyToast.info("O programa Marca do Bem chega em breve.")}
            className="inline-flex h-[100px] w-[100px] shrink-0 items-center justify-center rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <img src={seloMarcaDoBemFooter} alt="Marca do Bem — Selo Patrono Oficial" width={600} height={600} loading="lazy" className="max-h-[100px] max-w-[100px] object-contain" />
          </button>
          <div className="flex flex-col items-center gap-1.5">
            <img src={seloSsl.url} alt="Selo SSL certificado: site 100% seguro" width={150} height={80} loading="lazy" className="h-auto max-h-12 w-auto max-w-[92px] object-contain" />
            <a
              href="https://transparencyreport.google.com/safe-browsing/search?url=https:%2F%2Fshoppingdobem.com.br%2F&hl=pt_BR"
              aria-label="Consultar Shopping do Bem no Google Safe Browsing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <img src={seloGoogle.url} alt="Selo Google Safe Browsing" width={150} height={80} loading="lazy" className="h-auto max-h-12 w-auto max-w-[92px] object-contain" />
            </a>
          </div>
        </div>

        {/* Card institucional Promovendo o Bem — apenas no Brasil */}
        <div className="mt-10 border border-border rounded-xl bg-[#e8f1ec] p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
          <div className="shrink-0">
            <img src={logoPromovendoOBem} alt="Promovendo o Bem" width={259} height={96} loading="lazy" className="w-48 h-auto" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="font-display font-semibold text-foreground text-lg mb-2 flex items-center justify-center md:justify-start gap-2">
              <Heart className="h-5 w-5 fill-[#F9BC05] text-[#F9BC05]" />
              {t("footer.promovendo.title")}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("footer.promovendo.intro")} <strong className="text-foreground">{t("footer.promovendo.brand")}</strong>{t("footer.promovendo.body")}{" "}
              <span className="text-success font-semibold">{t("footer.promovendo.reinvested")}</span>.
            </p>
          </div>
          <div className="shrink-0 self-center">
            <Button asChild className="bg-[#F9BC05] text-foreground hover:bg-[#F9BC05]/90 border-none">
              <a href="https://www.promovendoobem.ong.br/" target="_blank" rel="noopener noreferrer">
                {t("footer.promovendo.cta")} <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        <div className="border-t border-border mt-10 pt-8 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} {brandName}. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";
